// Inbox reads and writes. The live updates socket (realtime.ts) refetches what
// changed; polling is the safety net: every 60 seconds while the socket is
// open, and every 5 (list) or 3 (open conversation) while it is not.
import { useRef } from 'react'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, unwrap } from './client'
import { useLiveState } from './liveState'
import { queryClient } from './queryClient'
import type { components } from './generated/schema'

export type Conversation = components['schemas']['ConversationSummary']
export type ConversationPage = components['schemas']['ConversationPage']
export type TimelineItem = components['schemas']['TimelineItem']
export type TimelinePage = components['schemas']['TimelinePage']
export type Message = components['schemas']['Message']
export type Note = components['schemas']['Note']
export type Teammate = components['schemas']['Teammate']
export type MeterEntry = components['schemas']['MeterEntry']
export type FailureReason = components['schemas']['FailureReason']

export type ListFilter = {
  status: 'open' | 'closed'
  assignee: string // me, unassigned, all, or a user id
  tagId: string | undefined
}

const keys = {
  list: (f: ListFilter) => ['conversations', f] as const,
  lists: ['conversations'] as const,
  one: (id: string) => ['conversation', id] as const,
  timeline: (id: string) => ['timeline', id] as const,
  teammates: ['teammates'] as const,
  meter: ['meter'] as const,
}

const SAFETY_NET_MS = 60_000

function usePollEvery(offlineMs: number) {
  return useLiveState() === 'live' ? SAFETY_NET_MS : offlineMs
}

// The socket said these conversations changed.
export function refreshConversations(ids: string[]) {
  for (const id of ids) {
    void queryClient.invalidateQueries({ queryKey: keys.one(id) })
    void queryClient.invalidateQueries({ queryKey: keys.timeline(id) })
  }
  void queryClient.invalidateQueries({ queryKey: keys.lists })
}

// The socket (re)connected: anything may have changed while it was down.
export function refreshInbox() {
  void queryClient.invalidateQueries({ queryKey: keys.lists })
  void queryClient.invalidateQueries({ queryKey: ['conversation'] })
  void queryClient.invalidateQueries({ queryKey: ['timeline'] })
}

export function useConversations(f: ListFilter) {
  const every = usePollEvery(5_000)
  return useInfiniteQuery({
    queryKey: keys.list(f),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }): Promise<ConversationPage> =>
      unwrap(
        await api.GET('/conversations', {
          params: {
            query: {
              status: f.status,
              assignee: f.assignee,
              ...(f.tagId ? { tagId: f.tagId } : {}),
              ...(pageParam ? { cursor: pageParam } : {}),
            },
          },
        }),
      ),
    getNextPageParam: (last) => last.nextCursor,
    refetchInterval: every,
  })
}

export function useConversation(id: string) {
  const every = usePollEvery(3_000)
  return useQuery({
    queryKey: keys.one(id),
    queryFn: async (): Promise<Conversation> =>
      unwrap(await api.GET('/conversations/{conversationId}', { params: { path: { conversationId: id } } })),
    refetchInterval: every,
  })
}

export function useTimeline(id: string) {
  const every = usePollEvery(3_000)
  return useInfiniteQuery({
    queryKey: keys.timeline(id),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }): Promise<TimelinePage> =>
      unwrap(
        await api.GET('/conversations/{conversationId}/timeline', {
          params: { path: { conversationId: id }, query: pageParam ? { cursor: pageParam } : {} },
        }),
      ),
    getNextPageParam: (last) => last.nextCursor,
    refetchInterval: every,
  })
}

export function useTeammates() {
  return useQuery({
    queryKey: keys.teammates,
    queryFn: async (): Promise<Teammate[]> => unwrap(await api.GET('/users')),
    staleTime: 5 * 60_000,
  })
}

export function useMeter() {
  return useQuery({
    queryKey: keys.meter,
    queryFn: async (): Promise<MeterEntry[]> => unwrap(await api.GET('/meter')),
    refetchInterval: 60_000,
  })
}

// The composer calls the returned function on every keystroke. It sends at
// most one request every 10 seconds; the server drops repeats within 20
// across the team. A failure is silent: it only costs the indicator.
const TYPING_EVERY_MS = 10_000

export function useTyping(id: string) {
  const last = useRef(0)
  const typing = useMutation({
    mutationFn: async () => unwrap(await api.POST('/conversations/{conversationId}/typing', { params: { path: { conversationId: id } } })),
  })
  return () => {
    const now = Date.now()
    if (now - last.current < TYPING_EVERY_MS) return
    last.current = now
    typing.mutate()
  }
}

// Every change to a conversation refreshes it, its timeline and the lists.
function useRefresh(id: string) {
  const qc = useQueryClient()
  return () => {
    void qc.invalidateQueries({ queryKey: keys.one(id) })
    void qc.invalidateQueries({ queryKey: keys.timeline(id) })
    void qc.invalidateQueries({ queryKey: keys.lists })
  }
}

export function useSendReply(id: string) {
  const refresh = useRefresh(id)
  return useMutation({
    // One clientMessageId per message the user typed: a retry of the same
    // mutation reuses it, so a flaky network never sends twice.
    mutationFn: async (input: { text: string; clientMessageId: string }) =>
      unwrap(
        await api.POST('/conversations/{conversationId}/messages', {
          params: { path: { conversationId: id } },
          body: input,
        }),
      ),
    onSuccess: refresh,
  })
}

export function useAddNote(id: string) {
  const refresh = useRefresh(id)
  return useMutation({
    mutationFn: async (body: string) =>
      unwrap(
        await api.POST('/conversations/{conversationId}/notes', {
          params: { path: { conversationId: id } },
          body: { body },
        }),
      ),
    onSuccess: refresh,
  })
}

export function useDeleteNote(id: string) {
  const refresh = useRefresh(id)
  return useMutation({
    mutationFn: async (noteId: string) => {
      const res = await api.DELETE('/conversations/{conversationId}/notes/{noteId}', {
        params: { path: { conversationId: id, noteId } },
      })
      if (!res.response.ok) unwrap(res)
    },
    onSuccess: refresh,
  })
}

export function useAssign(id: string) {
  const refresh = useRefresh(id)
  return useMutation({
    mutationFn: async (userId: string | undefined) =>
      unwrap(
        await api.PUT('/conversations/{conversationId}/assignee', {
          params: { path: { conversationId: id } },
          body: userId ? { userId } : {},
        }),
      ),
    onSuccess: refresh,
  })
}

export function useSetStatus(id: string) {
  const refresh = useRefresh(id)
  return useMutation({
    mutationFn: async (status: 'open' | 'closed') =>
      unwrap(
        await api.PUT('/conversations/{conversationId}/status', {
          params: { path: { conversationId: id } },
          body: { status },
        }),
      ),
    onSuccess: refresh,
  })
}

export type TemplateTerms = components['schemas']['TemplateTerms']
export type SendTemplateInput = components['schemas']['SendTemplate']

// What a template to this conversation's customer costs and whether one may
// be sent. Prices come from the rate card; the server decides.
export function useTemplateTerms(id: string) {
  return useQuery({
    queryKey: ['template-terms', id] as const,
    queryFn: async (): Promise<TemplateTerms> =>
      unwrap(await api.GET('/conversations/{conversationId}/template-terms', { params: { path: { conversationId: id } } })),
  })
}

export function useSendTemplate(id: string) {
  const refresh = useRefresh(id)
  return useMutation({
    // The clientMessageId is chosen per template the user prepares, so a
    // retry after a network error never sends it twice.
    mutationFn: async (body: SendTemplateInput) =>
      unwrap(
        await api.POST('/conversations/{conversationId}/template-messages', {
          params: { path: { conversationId: id } },
          body,
        }),
      ),
    onSuccess: refresh,
  })
}
